import {
  PHYSICS, PhysicsEngine, SpatialHash, ArenaManager, OrbManager, UpgradeSystem,
} from '@push.io/shared';
import {
  PlayerState, PlayerInput, UpgradeTree,
} from '@push.io/shared';
import type { PublicPlayerAppearance } from '@push.io/shared';
import { GameState } from './game-state.js';
import { BotAI, type BotDifficulty } from './bot-ai.js';

const DEFAULT_APPEARANCE: PublicPlayerAppearance = {
  baseColorId: 'base_color_blue', skinId: null, patternId: null, flagId: null, letterId: null,
  accessoryId: null, trailId: null, nameplateId: null, badgeId: null, victoryEffectId: null,
};

const BOT_COLORS = [
  'base_color_red',
  'base_color_green',
  'base_color_yellow',
  'base_color_purple',
  'base_color_orange',
  'base_color_pink',
  'base_color_cyan',
  'base_color_white',
] as const;

export interface BotMatchConfig {
  botCount: number;
  difficulty: BotDifficulty;
  playerId: string;
  playerName: string;
  playerAppearance: PublicPlayerAppearance;
}

export interface BotMatchResult {
  won: boolean;
  placement: number;
  totalPlayers: number;
  eliminations: number;
  orbsCollected: number;
  survivalMs: number;
  exitReason: 'match_completed' | 'voluntary_leave';
}

type BotMatchCallback = {
  onUpdate: (player: PlayerState, players: PlayerState[]) => void;
  onEnd: (result: BotMatchResult) => void;
};

/**
 * Partida local "contra la máquina". Simula en el cliente la misma lógica que
 * el servidor (física, orbes, encogido de arena y eliminaciones) usando los
 * sistemas reutilizables de `@push.io/shared`. El bot mueve a los rivales con
 * la IA básica de `bot-ai.ts`.
 */
export class BotMatchManager {
  private state: GameState;
  private physics = new PhysicsEngine();
  private spatialHash = new SpatialHash();
  private arena: ArenaManager | null = null;
  private orbs: OrbManager | null = null;
  private upgrades = new UpgradeSystem();
  private ai = new BotAI('medium');
  private active = false;
  private callback: BotMatchCallback | null = null;
  private tickAccumulator = 0;
  private serverTick = 0;
  private localInput: { dirX: number; dirY: number } = { dirX: 0, dirY: 0 };
  private eliminationKills = new Map<string, number>();
  private meaningfulHits: Map<string, string> = new Map();
  private lastBotUpgradeTick = new Map<string, number>();
  private matchEnded = false;
  private startedAt = 0;
  private config: BotMatchConfig | null = null;

  constructor(state: GameState) {
    this.state = state;
  }

  isActive(): boolean {
    return this.active;
  }

  start(config: BotMatchConfig, callback: BotMatchCallback): void {
    this.config = config;
    this.callback = callback;
    this.ai = new BotAI(config.difficulty);
    this.active = true;
    this.matchEnded = false;
    this.tickAccumulator = 0;
    this.serverTick = 0;
    this.eliminationKills.clear();
    this.meaningfulHits.clear();
    this.lastBotUpgradeTick.clear();
    this.startedAt = performance.now();
    this.localInput = { dirX: 0, dirY: 0 };

    this.setupState(config);
    this.arena = new ArenaManager(this.state.arena);
    this.arena.startShrinking();
    this.orbs = new OrbManager(this.state.arena);
    this.orbs.spawnInitialOrbs();
    this.state.orbs = this.orbs.getOrbs();

    this.notify();
  }

  stop(): void {
    this.active = false;
    this.localInput = { dirX: 0, dirY: 0 };
    this.callback = null;
    this.arena = null;
    this.orbs = null;
  }

  handleInput(input: PlayerInput): void {
    const length = Math.hypot(input.dirX, input.dirY);
    const dirX = length > 1 ? input.dirX / length : input.dirX;
    const dirY = length > 1 ? input.dirY / length : input.dirY;
    this.localInput = { dirX, dirY };
    const local = this.state.getLocalPlayer();
    if (local) {
      local.desiredDirX = dirX;
      local.desiredDirY = dirY;
      if (length > 1e-8) local.angle = Math.atan2(dirY, dirX);
    }
  }

  purchase(stat: UpgradeTree): boolean {
    const local = this.state.getLocalPlayer();
    if (!local || !this.active || !local.alive) return false;
    const result = this.upgrades.processPurchase(local, {
      requestId: `${Date.now()}_local_${Math.random()}`,
      stat,
      expectedCurrentLevel: local.upgrades[stat],
      clientTimestamp: Date.now(),
    });
    this.notify();
    return result.success;
  }

  update(deltaMs: number): void {
    if (!this.active || !this.arena || !this.orbs) return;
    this.tickAccumulator += deltaMs;

    let steps = 0;
    while (this.tickAccumulator >= PHYSICS.TICK_INTERVAL && steps < 8) {
      this.tickAccumulator -= PHYSICS.TICK_INTERVAL;
      this.runTick();
      steps++;
      if (this.matchEnded) break;
    }

    this.notify();
  }

  private runTick(): void {
    if (this.matchEnded) return;
    this.serverTick++;
    this.state.tick = this.serverTick;

    this.applyBotDecisions();
    if (this.state.phase === 'playing') {
      const events = this.physics.update(this.state, this.spatialHash, this.serverTick);
      this.trackMeaningfulHits(events);
      this.arena!.update(this.serverTick);
      this.orbs!.update(this.serverTick, this.state.players);
      this.state.orbs = this.orbs!.getOrbs();
      this.checkEliminations();
      this.checkLocalElimination();
      this.checkVictoryCondition();
    }
  }

  private applyBotDecisions(): void {
    for (const player of this.state.players.values()) {
      if (player.id === this.state.playerId || !player.alive) continue;

      const decision = this.ai.decide(
        this.serverTick, player, this.state.players, this.state.orbs, this.state.arena.radius,
      );
      player.desiredDirX = decision.dirX;
      player.desiredDirY = decision.dirY;
      if (Math.hypot(decision.dirX, decision.dirY) > 1e-4) {
        player.angle = Math.atan2(decision.dirY, decision.dirX);
      }

      this.maybeBotUpgrade(player);
    }
  }

  private maybeBotUpgrade(player: PlayerState): void {
    const last = this.lastBotUpgradeTick.get(player.id) ?? 0;
    if (this.serverTick - last < 30) return;
    const stat = this.ai.decideUpgrade(player);
    if (!stat || player.orbs < 1) return;
    const result = this.upgrades.processPurchase(player, {
      requestId: `bot_${player.id}_${this.serverTick}`,
      stat,
      expectedCurrentLevel: player.upgrades[stat],
      clientTimestamp: Date.now(),
    });
    if (result.success) this.lastBotUpgradeTick.set(player.id, this.serverTick);
  }

  private trackMeaningfulHits(events: Array<{
    playerAId: string; playerBId: string; impulseA: { x: number; y: number };
    impulseB: { x: number; y: number };
  }>): void {
    for (const event of events) {
      const magB = Math.hypot(event.impulseB.x, event.impulseB.y);
      if (magB >= 0.08) this.meaningfulHits.set(event.playerBId, event.playerAId);
      const magA = Math.hypot(event.impulseA.x, event.impulseA.y);
      if (magA >= 0.08) this.meaningfulHits.set(event.playerAId, event.playerBId);
    }
  }

  private checkEliminations(): void {
    const eliminatedThisTick = this.getAlivePlayers()
      .map((player) => ({
        player,
        dist: Math.sqrt(player.x * player.x + player.y * player.y),
      }))
      .filter(({ player, dist }) => dist > this.state.arena.radius - player.radius)
      .sort((a, b) => b.dist - a.dist);

    for (const { player } of eliminatedThisTick) {
      const aliveCount = this.getAlivePlayers().length;
      if (aliveCount <= 1) break;
      this.eliminatePlayer(player.id, aliveCount);
    }
  }

  private eliminatePlayer(playerId: string, aliveCount: number): void {
    const player = this.state.players.get(playerId);
    if (!player || !player.alive) return;

    player.alive = false;
    player.eliminated = true;
    player.placement = aliveCount;

    const attacker = this.meaningfulHits.get(playerId);
    if (attacker) {
      const current = this.eliminationKills.get(attacker) ?? 0;
      this.eliminationKills.set(attacker, current + 1);
    }
  }

  private checkLocalElimination(): void {
    if (this.matchEnded || this.state.phase !== 'playing') return;
    const local = this.state.getLocalPlayer();
    if (local && !local.alive) {
      this.state.phase = 'ended';
      this.finishMatch();
    }
  }

  private checkVictoryCondition(): void {
    const alivePlayers = this.getAlivePlayers();
    if (alivePlayers.length <= 1 && this.state.phase === 'playing') {
      const winnerId = alivePlayers.length === 1 ? alivePlayers[0].id : null;
      this.state.phase = 'ended';
      if (winnerId) alivePlayers[0].placement = 1;
      this.finishMatch();
    }
  }

  private finishMatch(): void {
    if (this.matchEnded) return;
    this.matchEnded = true;
    this.active = false;

    const local = this.state.getLocalPlayer();

    const result: BotMatchResult = {
      won: !!local && !local.eliminated && local.alive,
      placement: local?.placement ?? this.getAlivePlayers().length,
      totalPlayers: this.config?.botCount ? this.config.botCount + 1 : 0,
      eliminations: this.eliminationKills.get(this.state.playerId) ?? 0,
      orbsCollected: local?.orbs ?? 0,
      survivalMs: performance.now() - this.startedAt,
      exitReason: 'match_completed',
    };

    this.callback?.onEnd(result);
    this.notify();
  }

  private setupState(config: BotMatchConfig): void {
    const players = new Map<string, PlayerState>();

    const local = makePlayer(
      config.playerId,
      config.playerName,
      config.playerAppearance,
    );
    const localSpawn = this.getSpawnPosition();
    local.x = localSpawn.x;
    local.y = localSpawn.y;
    players.set(local.id, local);

    for (let index = 0; index < config.botCount; index++) {
      const id = `bot-${index + 1}`;
      const spawn = this.getSpawnPosition();
      const appearance: PublicPlayerAppearance = {
        ...DEFAULT_APPEARANCE,
        baseColorId: BOT_COLORS[index % BOT_COLORS.length],
      };
      players.set(id, makePlayer(id, `Bot ${index + 1}`, appearance, spawn.x, spawn.y));
    }

    this.state.players = players;
    this.state.playerId = config.playerId;
    this.state.localPlayer = local;
    this.state.phase = 'playing';
    this.state.tick = 0;
    this.state.playerAppearances.clear();
    for (const player of players.values()) {
      this.state.playerAppearances.set(player.id, player.appearance);
    }
    this.state.arena = {
      centerX: PHYSICS.ARENA_CENTER_X,
      centerY: PHYSICS.ARENA_CENTER_Y,
      radius: PHYSICS.ARENA_INITIAL_RADIUS,
      initialRadius: PHYSICS.ARENA_INITIAL_RADIUS,
      minRadius: PHYSICS.ARENA_MIN_RADIUS,
      brokenSegments: [],
      collapsePercent: 0,
    };
    this.state.orbs = [];
  }

  private getSpawnPosition(): { x: number; y: number } {
    const angle = Math.random() * Math.PI * 2;
    const radius = PHYSICS.ARENA_INITIAL_RADIUS * 0.7 * Math.random();
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  }

  private getAlivePlayers(): PlayerState[] {
    return Array.from(this.state.players.values()).filter((p) => p.alive);
  }

  private notify(): void {
    if (!this.callback) return;
    const local = this.state.getLocalPlayer();
    if (!local) return;
    this.callback.onUpdate(
      { ...local, upgrades: { ...local.upgrades } },
      Array.from(this.state.players.values())
        .map((item) => ({ ...item, upgrades: { ...item.upgrades } })),
    );
  }
}

function makePlayer(
  id: string,
  name: string,
  appearance: PublicPlayerAppearance,
  x: number = 0,
  y: number = 0,
): PlayerState {
  return {
    id, name, x, y, vx: 0, vy: 0, angle: Math.random() * Math.PI * 2,
    radius: PHYSICS.PLAYER_RADIUS, alive: true, orbs: 0,
    upgrades: { push: 0, weight: 0, speed: 0, control: 0 },
    desiredDirX: 0, desiredDirY: 0, knockbackTicks: 0, impactImmunityUntilTick: 0,
    recoveryTimer: 0, sequence: 0, appearance,
    eliminated: false, placement: 0,
  };
}